import { useState } from 'react'
import './index.scss'

import { useAuth } from '@auth/AuthProvider'
import Modal, { ModalButtonStyle } from '@components/Modal'
import CampusController, { CampusAttributes } from '@data/controller/CampusController'
import { Campus } from '@data/ChurchDataProvider'

import closeIcon from 'url:@icon/close.svg';

export default function CampusModal(props: {
  isOpen: boolean,
  campus?: Campus | null,
  close: () => void,
  onSaved: (campus: Campus) => void
}) {
  const auth = useAuth();
  const modalTitle = props.campus?.attributes.name ?? 'New Campus';

  const [name, setName] = useState(props.campus?.attributes.name ?? '');
  const [street, setStreet] = useState(props.campus?.attributes.address?.street ?? '');
  const [city, setCity] = useState(props.campus?.attributes.address?.city ?? '');
  const [state, setState] = useState(props.campus?.attributes.address?.state ?? '');
  const [zip, setZip] = useState(props.campus?.attributes.address?.zip ?? '');
  const [country, setCountry] = useState(props.campus?.attributes.address?.country ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveCampus = async () => {
    if (!auth.churchId) {
      setError('Missing church context. Please refresh and try again.');
      return;
    }

    const trimmedName = name.trim();

    if (trimmedName.length === 0) {
      setError('Please give this campus a name.');
      return;
    }

    const addressFields = [street, city, state, zip, country].map((value) => value.trim());
    const hasAnyAddressField = addressFields.some((value) => value.length > 0);
    const hasAllAddressFields = addressFields.every((value) => value.length > 0);

    if (hasAnyAddressField && !hasAllAddressFields) {
      setError('If you add an address, please fill out every address field.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const attributes: CampusAttributes = {
        name: trimmedName,
        address: hasAllAddressFields ? {
          street: street.trim(),
          city: city.trim(),
          state: state.trim(),
          zip: zip.trim(),
          country: country.trim()
        } : undefined
      };

      const response = props.campus
        ? await CampusController.updateCampus(auth.churchId, props.campus.id, attributes, auth)
        : await CampusController.createCampus(auth.churchId, attributes, auth);

      if (!response.body) {
        setError(response.error || 'Unable to save this campus right now.');
        setIsSaving(false);
        return;
      }

      props.onSaved(response.body);
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    id='campus-modal'
    isOpen={props.isOpen}
    footerButtons={[
      {
        title: props.campus ? 'Save Changes' : 'Create Campus',
        style: ModalButtonStyle.FILLED,
        enabled: !isSaving,
        onClick: saveCampus
      },
      {
        title: 'Cancel',
        style: ModalButtonStyle.OUTLINE,
        enabled: !isSaving,
        onClick: props.close
      }
    ]}>
    <div className='campus-modal-content'>
      <div className='header'>
        <a className='close'
          aria-disabled={isSaving}
          className={isSaving ? 'close disabled' : 'close'}
          href='#'
          onClick={(e) => {
            e.preventDefault();

            if (isSaving) {
              return;
            }

            props.close();
          }}>
          <img className='icon' src={closeIcon} alt='Close icon' />
        </a>

        <h3 className='title'>{modalTitle}</h3>
      </div>

      {error ? <p className='save-error'>{error}</p> : null}

      <div className='body'>
        <div className='field-container'>
          <label htmlFor='campus-name'>Name</label>
          <input
            id='campus-name'
            type='text'
            name='campus-name'
            placeholder='Campus Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className='address-section'>
          <div className='section-copy'>
            <label>Address</label>
            <p className='caption'>Optional</p>
          </div>

          <div className='field-container'>
            <input
              id='campus-street'
              type='text'
              placeholder='Street'
              value={street}
              onChange={(e) => setStreet(e.target.value)}
            />
          </div>

          <div className='address-grid'>
            <div className='field-container'>
              <input
                id='campus-city'
                type='text'
                placeholder='City'
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>

            <div className='field-container'>
              <input
                id='campus-state'
                type='text'
                placeholder='State'
                value={state}
                onChange={(e) => setState(e.target.value)}
              />
            </div>

            <div className='field-container'>
              <input
                id='campus-zip'
                type='text'
                placeholder='ZIP'
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </div>

            <div className='field-container'>
              <input
                id='campus-country'
                type='text'
                placeholder='Country'
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
}
